<template>
	<AppPage>
		<AppCardPage>
			<div>
				<div class="flex items-center h-9.5">
					<h2 class="text-2xl">Beheer instellingen</h2>
				</div>
				<p class="text-sm text-secondary mb-4">
					Deze waarden worden automatisch ingevuld bij een nieuw dossier. Per
					dossier kun je afwijken.
				</p>
				<div class="grid grid-cols-2 gap-4 max-w-lg items-center">
					<label class="text-sm">Notaris levering</label>
					<AppFieldNumeric
						:model-value="defaults.notarisLevering"
						:value-type="ValueType.Currency"
						:precision="0"
						@update:model-value="
							(v: number | undefined) => updateDefault('notarisLevering', v)
						"
					/>
					<label class="text-sm">Notaris hypotheek</label>
					<AppFieldNumeric
						:model-value="defaults.notarisHypotheek"
						:value-type="ValueType.Currency"
						:precision="0"
						@update:model-value="
							(v: number | undefined) => updateDefault('notarisHypotheek', v)
						"
					/>
					<label class="text-sm">Taxatiekosten</label>
					<AppFieldNumeric
						:model-value="defaults.taxatiekosten"
						:value-type="ValueType.Currency"
						:precision="0"
						@update:model-value="
							(v: number | undefined) => updateDefault('taxatiekosten', v)
						"
					/>
					<label class="text-sm">Kosten hypotheekadvies</label>
					<AppFieldNumeric
						:model-value="defaults.kostenHypotheekadvies"
						:value-type="ValueType.Currency"
						:precision="0"
						@update:model-value="
							(v: number | undefined) =>
								updateDefault('kostenHypotheekadvies', v)
						"
					/>
				</div>
			</div>

			<div>
				<div class="flex items-center h-9.5">
					<h2 class="text-2xl">Serviceproviders</h2>
				</div>
				<ul
					v-if="status === 'complete' || serviceProviders.length > 0"
					class="flex flex-col gap-2"
				>
					<ServiceProviderRow
						v-for="serviceProvider in serviceProviders"
						:key="serviceProvider.id"
						:service-provider="serviceProvider"
						@update:connection-number="
							(connectionNumber) =>
								onUpdateServiceProviderConnection(
									serviceProvider,
									connectionNumber,
								)
						"
					/>
				</ul>
			</div>

			<div>
				<div class="flex items-center h-9.5">
					<h2 class="text-2xl">Geldverstrekkers</h2>
				</div>
				<ul
					v-if="lenderStatus === 'complete' || displayLenders.length > 0"
					class="grid grid-cols-3 gap-x-2 gap-y-4"
				>
					<LenderRow
						v-for="lender in displayLenders"
						:key="lender.name"
						:lender
						@update:connection-number="
							(connectionNumber) =>
								onUpdateLenderConnection(lender, connectionNumber)
						"
						@update:service-provider="
							(serviceProviderId) =>
								onUpdateLenderServiceProvider(lender, serviceProviderId)
						"
					/>
				</ul>
			</div>
		</AppCardPage>
	</AppPage>
</template>

<script lang="ts" setup>
import AppFieldNumeric from "@/components/input/AppFieldNumeric.vue";
import { ValueType } from "@/enums/ValueType";
import ServiceProviderRow from "@/components/service-providers/ServiceProviderRow.vue";
import type { LenderRecord } from "@/composables/useLender";
import { splitHdnValue } from "@/helpers/hdnHelpers";
import { useMainStore } from "@/store/useMainStore";
import { mutators, queries, type Schema } from "@loanlink-nl/zero";
import type { Row } from "@rocicorp/zero";
import { computed, reactive, watchEffect } from "vue";
import { useDebounceFn } from "@vueuse/core";
import { useQuery, useZero } from "@/composables/useZero";

type ActiveMortgageProvider = Row<Schema["tables"]["hdnProvider"]> & {
	supportedServiceProviders: Readonly<
		Row<Schema["tables"]["hdnServiceProvider"]>[]
	>;
	connections: Readonly<
		(Row<Schema["tables"]["hdnLenderConnection"]> & {
			serviceProvider?: Row<Schema["tables"]["hdnServiceProvider"]>;
		})[]
	>;
};

type DisplayLender = LenderRecord & {
	provider: ActiveMortgageProvider;
	displayName: string;
};

type FinancingDefaults = {
	notarisLevering?: number;
	notarisHypotheek?: number;
	taxatiekosten?: number;
	kostenHypotheekadvies?: number;
};

const mainStore = useMainStore();
const z = useZero();

const serverDefaults = computed<FinancingDefaults>(
	() => (mainStore.agency?.financingDefaults as FinancingDefaults) ?? {},
);

const defaults = reactive<FinancingDefaults>({});

watchEffect(() => {
	const server = serverDefaults.value;
	defaults.notarisLevering = server.notarisLevering;
	defaults.notarisHypotheek = server.notarisHypotheek;
	defaults.taxatiekosten = server.taxatiekosten;
	defaults.kostenHypotheekadvies = server.kostenHypotheekadvies;
});

const saveDefaults = useDebounceFn(() => {
	z.value.mutate(
		mutators.agency.updateFinancingDefaults({
			agencyId: mainStore.agency?.id ?? "",
			financingDefaults: { ...defaults },
		}),
	);
}, 500);

function updateDefault(
	key: keyof FinancingDefaults,
	value: number | undefined,
) {
	defaults[key] = value;
	saveDefaults();
}
const { data: serviceProviders, status } = useQuery(() =>
	queries.serviceProviders.withConnections({
		agencyId: mainStore.agency?.id ?? "",
	}),
);

const { data: lenders, status: lenderStatus } = useQuery(() =>
	queries.providers.activeMortgage({
		agencyId: mainStore.agency?.id ?? "",
	}),
);
const { data: lenderAssets } = useQuery(() => queries.lenders.all());

const providersByCode = computed(() => {
	if (!lenders.value) {
		return new Map();
	}

	return new Map(lenders.value.map((provider) => [provider.name, provider]));
});

const displayLenders = computed<DisplayLender[]>(() => {
	if (!lenderAssets.value) {
		return [];
	}

	return lenderAssets.value
		.flatMap((lender) => {
			if (!lender.hdnProviderCode) {
				return [];
			}

			const provider = providersByCode.value.get(lender.hdnProviderCode);
			if (!provider) {
				return [];
			}

			return [
				{
					...lender,
					provider,
					displayName: provider.label ?? splitHdnValue(provider.name)[1],
				} satisfies DisplayLender,
			];
		})
		.toSorted((a, b) => a.displayName.localeCompare(b.displayName));
});

function onUpdateServiceProviderConnection(
	serviceProvider: Row<Schema["tables"]["hdnServiceProvider"]>,
	connectionNumber?: string,
) {
	z.value.mutate(
		mutators.agencyServiceProviderConnection.upsertConnection({
			agencyId: mainStore.agency?.id ?? "",
			serviceProviderId: serviceProvider.id,
			connectionNumber,
		}),
	);
}

function onUpdateLenderConnection(
	lender: DisplayLender,
	connectionNumber?: string,
) {
	z.value.mutate(
		mutators.hdnLenderConnection.upsertConnection({
			agencyId: mainStore.agency?.id ?? "",
			lenderCode: lender.provider.name,
			connectionNumber,
			sendMethod: "direct",
		}),
	);
}

function onUpdateLenderServiceProvider(
	lender: DisplayLender,
	serviceProviderId: string,
) {
	z.value.mutate(
		mutators.hdnLenderConnection.upsertConnection({
			agencyId: mainStore.agency?.id ?? "",
			lenderCode: lender.provider.name,
			serviceProviderId,
			sendMethod: "service_provider",
		}),
	);
}
</script>
